import { useState, useMemo } from 'react';
import './App.css';

// Component: GraphicEQ with normalized overlay
function GraphicEQ({ eqPreset, analysis }) {
  const copyLocal = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const bands = eqPreset?.bands || [];
  if (!bands.length) return null;

  const width = 900;
  const height = 320;
  const pad = { left: 55, right: 20, top: 25, bottom: 40 };
  const gMin = -12, gMax = 12;
  const fMin = 20, fMax = 20000;
  const freqTicks = [20,31.5,50,80,125,200,315,500,800,1000,2000,3150,5000,8000,10000,16000,20000];

  const xScale = (f) => {
    const r = (Math.log10(f) - Math.log10(fMin)) / (Math.log10(fMax) - Math.log10(fMin));
    return pad.left + r * (width - pad.left - pad.right);
  };
  const yScale = (g) => {
    const r = (g - gMin) / (gMax - gMin);
    return height - pad.bottom - r * (height - pad.top - pad.bottom);
  };

  // Normalizzazione overlay: centra measured/target su mediana correzione (se disponibile)
  const normalizedOverlay = useMemo(() => {
    const measured = analysis?.measured_spl || [];
    const target = analysis?.target_spl || [];
    const freqs = analysis?.frequency || [];
    if (!measured.length || !target.length || measured.length !== target.length) return null;
    const correction = measured.map((m,i) => target[i] - m);
    const sorted = [...correction].sort((a,b)=>a-b);
    const mid = sorted.length ? sorted[Math.floor(sorted.length/2)] : 0;
    const measuredNorm = measured.map(m => m + mid);
    const targetNorm = target.map(t => t + mid);
    return { freqs, measuredNorm, targetNorm };
  }, [analysis]);

  const curvePath = useMemo(() => {
    if (bands.length < 2) return '';
    const pts = bands.map(b => ({ x: xScale(b.frequency), y: yScale(b.gain) }));
    const d = [];
    d.push(`M ${pts[0].x} ${pts[0].y}`);
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
    }
    return d.join(' ');
  }, [bands]);

  const measuredPath = useMemo(() => {
    if (!normalizedOverlay) return '';
    const { freqs, measuredNorm } = normalizedOverlay;
    if (freqs.length < 2) return '';
    const pts = freqs.map((f,i)=>({x:xScale(f), y:yScale(measuredNorm[i])}));
    const d = [`M ${pts[0].x} ${pts[0].y}`];
    for (let i=0;i<pts.length-1;i++) {
      const p0 = pts[i-1]||pts[i];
      const p1 = pts[i];
      const p2 = pts[i+1];
      const p3 = pts[i+2]||p2;
      const cp1x = p1.x + (p2.x - p0.x)/6;
      const cp1y = p1.y + (p2.y - p0.y)/6;
      const cp2x = p2.x - (p3.x - p1.x)/6;
      const cp2y = p2.y - (p3.y - p1.y)/6;
      d.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
    }
    return d.join(' ');
  }, [normalizedOverlay]);

  const targetPath = useMemo(() => {
    if (!normalizedOverlay) return '';
    const { freqs, targetNorm } = normalizedOverlay;
    if (freqs.length < 2) return '';
    const pts = freqs.map((f,i)=>({x:xScale(f), y:yScale(targetNorm[i])}));
    const d = [`M ${pts[0].x} ${pts[0].y}`];
    for (let i=0;i<pts.length-1;i++) {
      const p0 = pts[i-1]||pts[i];
      const p1 = pts[i];
      const p2 = pts[i+1];
      const p3 = pts[i+2]||p2;
      const cp1x = p1.x + (p2.x - p0.x)/6;
      const cp1y = p1.y + (p2.y - p0.y)/6;
      const cp2x = p2.x - (p3.x - p1.x)/6;
      const cp2y = p2.y - (p3.y - p1.y)/6;
      d.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
    }
    return d.join(' ');
  }, [normalizedOverlay]);

  return (
    <div className="eq-visualizer">
      <h4 style={{marginBottom: '0.75rem', color: 'var(--text-primary)'}}>📊 Graphic EQ ({bands.length} bands)</h4>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
        <rect x={0} y={0} width={width} height={height} fill="#0b0b0b" rx={8} />
        {freqTicks.map(f => (
          <line key={f} x1={xScale(f)} x2={xScale(f)} y1={pad.top} y2={height - pad.bottom} stroke="#1f2937" strokeDasharray="2 4" />
        ))}
        {[-12,-9,-6,-3,0,3,6,9,12].map(g => (
          <line key={g} x1={pad.left} x2={width - pad.right} y1={yScale(g)} y2={yScale(g)} stroke={g===0? '#334155':'#1f2937'} strokeWidth={g===0?1.4:1} />
        ))}
        <line x1={pad.left} x2={width - pad.right} y1={height - pad.bottom} y2={height - pad.bottom} stroke="#475569" />
        <line x1={pad.left} x2={pad.left} y1={pad.top} y2={height - pad.bottom} stroke="#475569" />
        {/* Target / Measured overlay (normalizzato) */}
        {normalizedOverlay && targetPath && (
          <path d={targetPath} fill="none" stroke="#6366f1" strokeWidth={1.6} strokeDasharray="4 3" />
        )}
        {normalizedOverlay && measuredPath && (
          <path d={measuredPath} fill="none" stroke="#f59e0b" strokeWidth={1.6} />
        )}
        {/* Curva EQ correttiva */}
        <path d={curvePath} fill="none" stroke="#38bdf8" strokeWidth={2} />
        <path d={curvePath + ` L ${xScale(bands[bands.length-1].frequency)} ${yScale(0)} L ${xScale(bands[0].frequency)} ${yScale(0)} Z`} fill="rgba(56,189,248,0.10)" />
        {bands.map((b,i) => {
          // Riduce etichette per 31 bande: mostra solo metà per evitare sovrapposizione
          const showLabel = bands.length <= 16 || i % 2 === 0;
          return (
            <g key={b.frequency}>
              <circle cx={xScale(b.frequency)} cy={yScale(b.gain)} r={6} fill={b.gain>=0? '#10b981':'#ef4444'} stroke="#0f172a" strokeWidth={2} />
              {showLabel && (
                <text x={xScale(b.frequency)} y={yScale(b.gain)-10} fontSize="10" textAnchor="middle" fill="#94a3af">
                  {b.gain>0?'+':''}{b.gain.toFixed(1)}dB
                </text>
              )}
            </g>
          );
        })}
        {freqTicks.map(f => (
          <text key={`label-${f}`} x={xScale(f)} y={height - pad.bottom + 14} fontSize="10" textAnchor="middle" fill="#64748b">{f>=1000? (f/1000).toFixed(1)+'k': f}</text>
        ))}
        {[-12,-6,0,6,12].map(g => (
          <text key={`glabel-${g}`} x={8} y={yScale(g)+3} fontSize="10" fill="#64748b">{g} dB</text>
        ))}
        {eqPreset?.preamp !== undefined && (
          <text x={width - pad.right - 5} y={pad.top + 5} fontSize="12" textAnchor="end" fill="#94a3af">Preamp {eqPreset.preamp.toFixed(1)} dB</text>
        )}
      </svg>
      <div className="eq-text-output">
        <h4>📝 Text Output:</h4>
        <pre className="eq-code-block">{eqPreset?.text_output || ''}</pre>
        <button className="copy-button" onClick={() => copyLocal(eqPreset?.text_output || '')}>📋 Copy EQ Settings</button>
        {bands.length > 16 && (
          <div style={{marginTop:'0.5rem', fontSize:11, color:'#64748b'}}>
            Etichette ridotte (mostrata 1 ogni 2) per evitare sovrapposizione. Passa a meno bande o parametric EQ per più dettaglio.
          </div>
        )}
      </div>
      {normalizedOverlay && (
        <div style={{marginTop:'0.75rem', fontSize:12, color:'#94a3af'}}>
          Overlay normalizzato su mediana correzione • Measured (arancio) Target (violetto) • EQ (azzurro)
        </div>
      )}
      {analysis?.problems_detected?.length > 0 && (
        <div className="recommendations" style={{marginTop: '0.5rem'}}>
          <h4>⚠️ Problemi individuati:</h4>
          <ul>
            {analysis.problems_detected.slice(0,6).map((p,i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function App() {
  // Step 1: Audio Analysis
  const [audioFile, setAudioFile] = useState(null);
  const [skipAudio, setSkipAudio] = useState(false);
  const [audioAnalyzing, setAudioAnalyzing] = useState(false);
  const [audioResult, setAudioResult] = useState(null);
  const [audioError, setAudioError] = useState(null);

  // Step 2: CAD Analysis
  const [cadFile, setCadFile] = useState(null);
  const [skipCAD, setSkipCAD] = useState(false);
  const [cadAnalyzing, setCadAnalyzing] = useState(false);
  const [cadResult, setCadResult] = useState(null);
  const [cadError, setCadError] = useState(null);

  // EQ Settings
  const [targetCurve, setTargetCurve] = useState('flat');
  const [eqFormat, setEqFormat] = useState('graphic_eq_31');
  const [targetFile, setTargetFile] = useState(null);

  // Backend base URL dinamico:
  // - In sviluppo: lasciando stringa vuota usa il proxy Vite (vedi vite.config.js) verso localhost:8001
  // - In produzione (Vercel / altro): definire VITE_BACKEND_URL (es: https://your-backend.example.com)
  const ENV_BASE = (import.meta.env?.VITE_BACKEND_URL || '').trim();
  const API_BASE = ENV_BASE.replace(/\/$/, ''); // rimuove eventuale slash finale
  const resolvedBase = API_BASE || '';
  console.log('[AutoEQ] API_BASE risolto:', resolvedBase || '(proxy relativo)', 'ENV VITE_BACKEND_URL:', ENV_BASE || 'non impostato');

  // Helper centralizzato per fetch con diagnosi avanzata
  async function apiFetch(path, { method = 'GET', body } = {}, contextLabel = 'generic') {
    const url = `${resolvedBase}${path.startsWith('/') ? path : '/' + path}`;
    const t0 = performance.now();
    let response;
    try {
      response = await fetch(url, { method, body });
    } catch (e) {
      // Network / CORS / DNS error (fetch genera TypeError)
      const msg = 'NetworkError: impossibile raggiungere il backend. Possibili cause:\n' +
        '- Backend non avviato o porta errata\n' +
        `- CORS bloccato (verifica allow_origins)\n- URL base non valido: '${resolvedBase || '(relativo)'}'\n` +
        '- Se sei su Vercel: devi impostare VITE_BACKEND_URL verso un host attivo con /api/*';
      throw new Error(msg);
    }
    const dt = (performance.now() - t0).toFixed(0);
    const contentType = response.headers.get('Content-Type') || '';
    const rawBody = await response.text();
    console.log(`[Fetch:${contextLabel}] ${response.status} ${method} ${url} (${dt}ms) CT='${contentType}' bodyLen=${rawBody.length}`);

    if (!rawBody) {
      throw new Error('Risposta vuota dal backend. Verifica che il server sia attivo e che il path /api sia corretto.');
    }
    if (!contentType.includes('application/json')) {
      throw new Error(`Risposta non-JSON (Content-Type: ${contentType}). Snippet: ${rawBody.slice(0,120)}`);
    }
    let json;
    try {
      json = JSON.parse(rawBody);
    } catch (e) {
      throw new Error(`Parsing JSON fallito. Snippet: ${rawBody.slice(0,120)}`);
    }
    // Gestione 404 distinta
    if (response.status === 404) {
      throw new Error(`HTTP 404: endpoint non trovato su '${url}'. Se sei su Vercel probabilmente stai colpendo solo il frontend senza backend.`);
    }
    if (!response.ok || json.success === false) {
      const detail = json.error || json.detail || `Errore HTTP ${response.status}`;
      throw new Error(detail);
    }
    return json;
  }

  // Handle Audio File Selection
  const handleAudioFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioError(null);
    }
  };

  // Handle CAD File Selection
  const handleCadFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCadFile(file);
      setCadError(null);
    }
  };

  // Handle Target File Selection
  const handleTargetFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTargetFile(file);
    }
  };

  // Analyze Audio File
  const handleAnalyzeAudio = async () => {
    if (skipAudio) {
      setAudioResult({ skipped: true });
      return;
    }

    if (!audioFile) {
      setAudioError('Please select an audio file (.gll/.txt/.csv/.crv)');
      return;
    }

    setAudioAnalyzing(true);
    setAudioError(null);
    setAudioResult(null);

    const formData = new FormData();
    formData.append('file', audioFile);
    formData.append('target_curve', targetCurve);
    formData.append('eq_format', eqFormat);
    
    // Aggiungi file target se presente
    if (targetFile) {
      formData.append('target_file', targetFile);
    }

    try {
      const data = await apiFetch('/api/analyze', { method: 'POST', body: formData }, 'audio');
      const mappedResult = {
        file_info: {
          original_name: data.file_info?.filename || audioFile.name,
          size: data.file_info?.data_points ? `${data.file_info.data_points} points` : 'N/A',
          type: data.file_info?.format || 'unknown'
        },
        target_curve: targetCurve,
        eq_format: data.eq_format || eqFormat,
        eq_settings: data.eq_settings || data.eq_preset?.bands || [],
        eq_string: data.eq_string || data.eq_preset?.text_output || data.eq_preset?.string_format || '',
        eq_preset: data.eq_preset || null,
        analysis: data.analysis || null,
        recommendations: Array.isArray(data.recommendations) ? data.recommendations.join('\n') : (data.recommendations || ''),
        spatial_data: data.file_info?.spatial_metadata || null
      };
      setAudioResult(mappedResult);
    } catch (error) {
      console.error('[Audio] Error:', error);
      setAudioError(error.message || 'Failed to analyze audio file');
    } finally { setAudioAnalyzing(false); }
  };

  // Analyze CAD File
  const handleAnalyzeCAD = async () => {
    if (skipCAD) {
      setCadResult({ skipped: true });
      return;
    }

    if (!cadFile) {
      setCadError('Please select a CAD file (.dxf)');
      return;
    }

    setCadAnalyzing(true);
    setCadError(null);
    setCadResult(null);

    const formData = new FormData();
    formData.append('file', cadFile);

    try {
      const data = await apiFetch('/api/analyze', { method: 'POST', body: formData }, 'cad');
      const mappedResult = {
        file_info: {
          original_name: data.file_info?.filename || cadFile.name,
          size: data.file_info?.message || 'CAD file',
          type: data.file_info?.format || 'dxf'
        },
        spatial_data: data.file_info?.spatial_metadata || data.parsed_data?.spatial_metadata || null,
        recommendations: Array.isArray(data.recommendations) ? data.recommendations.join('\n') : (data.recommendations || data.analysis?.note || '')
      };
      setCadResult(mappedResult);
    } catch (error) {
      console.error('[CAD] Error:', error);
      setCadError(error.message || 'Failed to analyze CAD file');
    } finally { setCadAnalyzing(false); }
  };

  // Copy EQ to Clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('EQ settings copied to clipboard!');
  };

  // Render Parametric EQ Visualizer (4/8 bands) with HPF/LPF overlay
  const renderParametricVisualizer = (preset) => {
    if (!preset || !String(preset.type).includes('parametric')) return null;
    const bands = preset.bands || [];
    const filters = preset.filters || {};

    const width = 900;
    const height = 260;
    const pad = { left: 48, right: 24, top: 18, bottom: 28 };
    const fMin = 20, fMax = 20000;
    const gMin = -12, gMax = 12;

    const xScale = (f) => {
      const r = (Math.log10(f) - Math.log10(fMin)) / (Math.log10(fMax) - Math.log10(fMin));
      return pad.left + r * (width - pad.left - pad.right);
    };
    const yScale = (g) => {
      const r = (g - gMin) / (gMax - gMin);
      return height - pad.bottom - r * (height - pad.top - pad.bottom);
    };

    const xTicksFreqs = [20, 31.5, 50, 80, 125, 200, 315, 500, 800, 1000, 2000, 5000, 10000, 20000];
    const yTicksVals = [-12, -6, 0, 6, 12];

    return (
      <div style={{ marginTop: '1rem', background: 'var(--bg-dark)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 10 }}>
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
          <rect x={0} y={0} width={width} height={height} fill="#0b0b0b" />
          {xTicksFreqs.map((f) => (
            <g key={f}>
              <line x1={xScale(f)} x2={xScale(f)} y1={pad.top} y2={height - pad.bottom} stroke="#222" />
              <text x={xScale(f)} y={height - 8} fontSize="10" textAnchor="middle" fill="#9ca3af">{f >= 1000 ? `${(f/1000).toFixed(1)}k` : f}Hz</text>
            </g>
          ))}
          {yTicksVals.map((g) => (
            <g key={g}>
              <line x1={pad.left} x2={width - pad.right} y1={yScale(g)} y2={yScale(g)} stroke="#222" />
              <text x={8} y={yScale(g) + 3} fontSize="10" fill="#9ca3af">{g} dB</text>
            </g>
          ))}

          {/* HPF / LPF areas */}
          {filters?.hpf?.enabled && (
            <path d={`M ${xScale(fMin)} ${yScale(gMin)} L ${xScale(filters.hpf.frequency)} ${yScale(gMin)} L ${xScale(filters.hpf.frequency)} ${yScale(0)} L ${xScale(fMin)} ${yScale(0)} Z`} fill="rgba(14,165,233,0.14)" />
          )}
          {filters?.lpf?.enabled && (
            <path d={`M ${xScale(filters.lpf.frequency)} ${yScale(gMin)} L ${xScale(fMax)} ${yScale(gMin)} L ${xScale(fMax)} ${yScale(0)} L ${xScale(filters.lpf.frequency)} ${yScale(0)} Z`} fill="rgba(14,165,233,0.12)" />
          )}

          {/* Parametric band points */}
          {(bands || []).map((b, i) => (
            <g key={i}>
              <circle cx={xScale(b.frequency || 1000)} cy={yScale(b.gain || 0)} r={6} fill="#4FC3F7" stroke="#0EA5E9" />
              <text x={xScale(b.frequency || 1000)} y={yScale((b.gain || 0)) - 10} fontSize="10" textAnchor="middle" fill="#9ca3af">
                {(b.gain || 0) > 0 ? '+' : ''}{(b.gain || 0).toFixed(1)} dB
              </text>
            </g>
          ))}
        </svg>

        {/* Legend / chips */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: 8 }}>
          {filters?.hpf && (
            <div style={{background:'#111',border:'1px solid #1f2937',borderRadius:6,padding:'6px 10px',color:'#9ca3af'}}>HPF: {filters.hpf.frequency} Hz · {filters.hpf.slope} dB/oct</div>
          )}
          {filters?.lpf?.enabled && (
            <div style={{background:'#111',border:'1px solid #1f2937',borderRadius:6,padding:'6px 10px',color:'#9ca3af'}}>LPF: {filters.lpf.frequency} Hz · {filters.lpf.slope} dB/oct</div>
          )}
          <div style={{background:'#111',border:'1px solid #1f2937',borderRadius:6,padding:'6px 10px',color:'#9ca3af'}}>Bands: {bands.length}</div>
          <div style={{background:'#111',border:'1px solid #1f2937',borderRadius:6,padding:'6px 10px',color:'#9ca3af'}}>Preamp: {preset.preamp?.toFixed?.(1) ?? 0} dB</div>
        </div>

        {/* Text list */}
        <div style={{ marginTop: '0.75rem' }}>
          <pre className="eq-code-block" style={{maxHeight: 220}}>
{(bands || []).map((b, i) => `${String(i+1).padStart(2,'0')}. ${(b.frequency ?? 0).toString().padEnd(6)} Hz  ${(b.gain ?? 0)>=0?'+':''}${(b.gain ?? 0).toFixed(1)} dB  Q=${(b.q ?? 1.4).toFixed(2)}`).join('\n')}
{filters?.hpf?`\nHPF ${filters.hpf.frequency} Hz  ${filters.hpf.slope} dB/oct`:''}
{filters?.lpf?.enabled?`\nLPF ${filters.lpf.frequency} Hz  ${filters.lpf.slope} dB/oct`:''}
          </pre>
        </div>
      </div>
    );
  };

  // Funzione per esportare CSV con dati scientifici
  const exportCSV = () => {
    if (!audioResult?.analysis) {
      alert('No analysis data available');
      return;
    }

    const analysis = audioResult.analysis;
    const hasTarget = analysis.target_trace && analysis.target_trace.frequency && analysis.target_trace.spl;
    const hasCorrection = analysis.correction_trace && analysis.correction_trace.frequency && analysis.correction_trace.db;

    if (!hasTarget && !hasCorrection) {
      alert('No target or correction data available. Upload a target file to get precise numerical traces.');
      return;
    }

    // Prepara intestazioni
    let csvContent = 'Frequency (Hz),Measured SPL (dB)';
    if (hasTarget) csvContent += ',Target SPL (dB)';
    if (hasCorrection) csvContent += ',Correction (dB)';
    csvContent += '\n';

    // Prepara dati - usa le frequenze misurate come riferimento
    const frequencies = analysis.frequency || [];
    const measuredSPL = analysis.spl || [];
    const targetSPL = hasTarget ? analysis.target_trace.spl : [];
    const correctionDB = hasCorrection ? analysis.correction_trace.db : [];

    for (let i = 0; i < frequencies.length; i++) {
      let row = `${frequencies[i]},${measuredSPL[i] || ''}`;
      if (hasTarget) row += `,${targetSPL[i] || ''}`;
      if (hasCorrection) row += `,${correctionDB[i] || ''}`;
      csvContent += row + '\n';
    }

    // Crea e scarica il file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const filename = `eq_analysis_${audioFile?.name?.replace(/\.[^.]+$/, '') || 'export'}_${Date.now()}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>🎚️ Auto-EQ Live PA System</h1>
        <p>Professional speaker equalization for outdoor spaces</p>
      </header>

      <main className="app-main">
        {/* STEP 1: AUDIO ANALYSIS */}
        <section className="upload-section">
          <h2>📊 Step 1: Audio Analysis</h2>
          
          <div className="skip-checkbox">
            <label>
              <input 
                type="checkbox" 
                checked={skipAudio} 
                onChange={(e) => {
                  setSkipAudio(e.target.checked);
                  if (e.target.checked) {
                    setAudioFile(null);
                    setAudioResult(null);
                    setAudioError(null);
                  }
                }}
              />
              Skip Audio Analysis (use only CAD)
            </label>
          </div>

          {!skipAudio && (
            <>
              <div className="file-input-group">
                <label className="file-label">
                  <strong>Carica File di Misura (da Smaart, REW, etc.):</strong>
                  <div className="file-types">
                    <div>
                      � <strong>.txt, .csv, .crv</strong> - File di misurazione da microfono (es. Smaart, Open Sound Meter, REW).
                    </div>
                     <div>
                      🔊 <strong>.gll</strong> - Dati anecoici del costruttore (alternativa al file target).
                    </div>
                  </div>
                </label>
                <input 
                  type="file" 
                  accept=".gll,.txt,.csv,.crv"
                  onChange={handleAudioFileChange}
                  disabled={audioAnalyzing}
                />
                {audioFile && (
                  <p className="file-selected">✅ Misura: {audioFile.name}</p>
                )}
              </div>

              <div className="file-input-group">
                <label className="file-label">
                  <strong>Carica File Target (Opzionale, es. dati GLL costruttore):</strong>
                  <div className="file-types">
                    <div>🎯 <strong>.txt, .csv, .crv, .gll</strong> - Curva di risposta ideale da raggiungere.</div>
                    <div style={{fontSize: '12px', color: '#94a3af', marginTop: '4px'}}>
                      Se non fornito, l'EQ mirerà a una curva standard (es. Flat). Se carichi qui un file GLL, verrà usato come target anecoico.
                    </div>
                  </div>
                </label>
                <input 
                  type="file" 
                  accept=".txt,.csv,.crv"
                  onChange={handleTargetFileChange}
                  disabled={audioAnalyzing}
                />
                {targetFile && (
                  <p className="file-selected">🎯 Target: {targetFile.name}</p>
                )}
              </div>

              <div className="settings-grid">
                <div className="setting-group">
                  <label>Target Curve:</label>
                  <select 
                    value={targetCurve} 
                    onChange={(e) => setTargetCurve(e.target.value)}
                    disabled={audioAnalyzing}
                  >
                    <option value="flat">Flat</option>
                    <option value="harman">Harman</option>
                    <option value="bass_boost">Bass Boost (+3dB below 200Hz)</option>
                    <option value="vocal_clarity">Vocal Clarity</option>
                  </select>
                </div>

                <div className="setting-group">
                  <label>EQ Format:</label>
                  <select 
                    value={eqFormat} 
                    onChange={(e) => setEqFormat(e.target.value)}
                    disabled={audioAnalyzing}
                  >
                    <optgroup label="📊 Graphic EQ">
                      <option value="graphic_eq_10">10-Band Graphic EQ</option>
                      <option value="graphic_eq_15">15-Band Graphic EQ</option>
                      <option value="graphic_eq_31">31-Band Graphic EQ</option>
                    </optgroup>
                    <optgroup label="🎛️ Parametric EQ">
                      <option value="parametric_eq_4">4-Band Parametric</option>
                      <option value="parametric_eq_8">8-Band Parametric</option>
                    </optgroup>
                    <optgroup label="🔊 Filters">
                      <option value="hpf_lpf">HPF + LPF</option>
                      <option value="shelf_eq">Shelf EQ</option>
                    </optgroup>
                    <optgroup label="⚡ Advanced">
                      <option value="fir">FIR Filter</option>
                      <option value="all_pass">All-Pass Filter</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              <button 
                className="analyze-button"
                onClick={handleAnalyzeAudio}
                disabled={audioAnalyzing || !audioFile}
              >
                {audioAnalyzing ? '⏳ Analyzing Audio...' : '🎚️ Analyze Audio'}
              </button>
            </>
          )}

          {skipAudio && (
            <div className="info-box">
              ℹ️ Audio analysis skipped. Proceed to CAD analysis for venue geometry only.
            </div>
          )}

          {audioError && (
            <div className="error-box">
              ❌ {audioError}
            </div>
          )}

          {audioResult && !audioResult.skipped && (
            <div className="result-section">
              <h3>✨ Audio Analysis Results</h3>
              
              {audioResult.file_info && (
                <div className="info-box">
                  <strong>File:</strong> {audioResult.file_info.original_name || 'Unknown'} ({audioResult.file_info.size || 'N/A'})<br/>
                  {audioResult.file_info.type && (
                    <><strong>Type:</strong> {audioResult.file_info.type.toUpperCase()}<br/></>
                  )}
                  <strong>Target:</strong> {audioResult.target_curve || 'flat'}
                  {targetFile && (
                    <>
                      <br/><strong>Custom Target File:</strong> {targetFile.name}
                    </>
                  )}
                </div>
              )}

              {/* Pulsante Export CSV per dati scientifici */}
              {audioResult.analysis && (audioResult.analysis.target_trace || audioResult.analysis.correction_trace) && (
                <button 
                  className="analyze-button"
                  onClick={exportCSV}
                  style={{marginTop: '1rem', background: '#10b981'}}
                >
                  📥 Export CSV (Measured/Target/Correction)
                </button>
              )}

              {(audioResult.eq_preset && audioResult.eq_preset.bands && audioResult.eq_preset.bands.length > 0) && (
                <div className="eq-results-container">
                  <div className="eq-format-info">
                    <strong>EQ Format:</strong> {audioResult.eq_preset?.type?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                    {audioResult.eq_preset.bands.length && ` (${audioResult.eq_preset.bands.length} bands)`}
                  </div>

                  {/* Visualizer: Parametric vs Graphic */}
                  {String(audioResult.eq_preset?.type || '').includes('parametric')
                    ? renderParametricVisualizer(audioResult.eq_preset)
                    : <GraphicEQ eqPreset={audioResult.eq_preset} analysis={audioResult.analysis} />
                  }
                </div>
              )}

              {audioResult.recommendations && (
                <div className="recommendations">
                  <h4>💡 AI Recommendations:</h4>
                  <p>{audioResult.recommendations}</p>
                </div>
              )}
            </div>
          )}

          {audioResult && audioResult.skipped && (
            <div className="info-box">
              ✅ Audio analysis skipped
            </div>
          )}
        </section>

        {/* STEP 2: CAD ANALYSIS */}
        <section className="upload-section">
          <h2>🏗️ Step 2: CAD / Venue Analysis</h2>
          
          <div className="skip-checkbox">
            <label>
              <input 
                type="checkbox" 
                checked={skipCAD} 
                onChange={(e) => {
                  setSkipCAD(e.target.checked);
                  if (e.target.checked) {
                    setCadFile(null);
                    setCadResult(null);
                    setCadError(null);
                  }
                }}
              />
              Skip CAD Analysis (use only audio)
            </label>
          </div>

          {!skipCAD && (
            <>
              <div className="file-input-group">
                <label className="file-label">
                  <strong>Upload CAD File:</strong>
                  <div className="file-types">
                    <div>🏗️ <strong>.dxf</strong> - AutoCAD / Soundvision venue geometry</div>
                  </div>
                </label>
                <input 
                  type="file" 
                  accept=".dxf"
                  onChange={handleCadFileChange}
                  disabled={cadAnalyzing}
                />
                {cadFile && (
                  <p className="file-selected">✅ Selected: {cadFile.name}</p>
                )}
              </div>

              <button 
                className="analyze-button"
                onClick={handleAnalyzeCAD}
                disabled={cadAnalyzing || !cadFile}
              >
                {cadAnalyzing ? '⏳ Analyzing CAD...' : '🏗️ Analyze CAD'}
              </button>
            </>
          )}

          {skipCAD && (
            <div className="info-box">
              ℹ️ CAD analysis skipped. Using audio analysis only.
            </div>
          )}

          {cadError && (
            <div className="error-box">
              ❌ {cadError}
            </div>
          )}

          {cadResult && !cadResult.skipped && (
            <div className="result-section">
              <h3>✨ CAD Analysis Results</h3>
              
              {cadResult.file_info && (
                <div className="info-box">
                  <strong>File:</strong> {cadResult.file_info.original_name || 'Unknown'} ({cadResult.file_info.size || 'N/A'})<br/>
                  {cadResult.file_info.type && (
                    <><strong>Type:</strong> {cadResult.file_info.type.toUpperCase()}</>
                  )}
                </div>
              )}

              {cadResult.spatial_data && (
                <div className="spatial-data">
                  <h4>📐 Spatial Data:</h4>
                  <pre className="eq-code-block">{JSON.stringify(cadResult.spatial_data, null, 2)}</pre>
                </div>
              )}

              {cadResult.recommendations && (
                <div className="recommendations">
                  <h4>💡 AI Recommendations:</h4>
                  <p>{cadResult.recommendations}</p>
                </div>
              )}
            </div>
          )}

          {cadResult && cadResult.skipped && (
            <div className="info-box">
              ✅ CAD analysis skipped
            </div>
          )}
        </section>
      </main>

      <footer className="app-footer">
        <p>Powered by Groq AI • Made for Live PA Systems</p>
      </footer>
    </div>
  );
}

export default App;
